import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { RichText, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { StringField, ImageField, LinkField, CheckboxField, VideoSitecoreField, TSitecoreContext, THeadingTag } from '../../../types/types';
import { TextColorField } from '../types/herobanner.types';
import { CustomText } from '../../@shared/customText';
import CTAButton, { ctaEnums } from '../../@shared/ctaButton';
import CustomExternalAnchor from '../../@shared/customExternalAnchor';
import Picture from '../../@shared/picture';
import FadeDownEffect from '../../@shared/fadeDownEffect';
import { googleClickCtaEvent } from '../../../utils/analytics';
import { shouldPioritizeComponentResources } from '../../../utils/component.utils';
import VideoPlayer from '../../@shared/videoPlayer';

// eslint-disable-next-line @typescript-eslint/interface-name-prefix
export interface HeroCarouselItemFields {
  id: string;
  image: ImageField;
  mobileImage: ImageField;
  title: StringField;
  content: StringField;
  link: LinkField;
  textColor: TextColorField;
  video: VideoSitecoreField;
  useVideo: CheckboxField;
}

// eslint-disable-next-line @typescript-eslint/interface-name-prefix
interface HeroCarouselItemProps {
  slideInfo?: {
    currentSlide: number;
    index: number;
    networkSpeed: number;
  };
  fields?: HeroCarouselItemFields;
  id?: string;
  headingTag?: THeadingTag;
  sitecoreContext: TSitecoreContext;
}

const HeroCarouselSlide: FunctionComponent<HeroCarouselItemProps> = ({ id, slideInfo, fields, sitecoreContext, headingTag }) => {
  const [mediaVisible, setMediaVisible] = useState({ video: false, image: !fields?.useVideo?.value });
  const textColor = fields?.textColor?.value || 'Black';
  const isEE = sitecoreContext?.pageEditing;
  useEffect(() => {
    // eslint-disable-line
    if (fields?.useVideo?.value && slideInfo?.currentSlide === slideInfo?.index && !mediaVisible.video) {
      setMediaVisible((prevState) => ({ ...prevState, video: true }));
    }

    if (slideInfo?.currentSlide === slideInfo?.index && !mediaVisible.image) {
      setMediaVisible((prevState) => ({ ...prevState, image: true }));
    }
  }, [fields, slideInfo, mediaVisible.video, mediaVisible.image]);

  const placeholderData = sitecoreContext?.route?.placeholders['jss-main'] || [];
  const shouldPioritize = useMemo(() => shouldPioritizeComponentResources(placeholderData, 'HeroBanner'), [placeholderData]);

  return (
    <div className={'slide__inner'} data-text-color={textColor}>
      {fields?.useVideo?.value && (sitecoreContext.pageEditing || (slideInfo && slideInfo.networkSpeed >= 15))
        ? mediaVisible.video && (
            <VideoPlayer link={fields?.video?.value?.href?.replace('sitecore/media-library', '-/media')} autoPlay={true} controls={false} muted={true} extraClassName={'slide__video-wrapper'} playsInline={true} loop={true}/>
          )
        : mediaVisible.image && (
            <Picture
              editorialImageProps={{
                id: id,
                desktopImage: fields?.image,
                tabletImage: fields?.mobileImage,
                mobileImage: fields?.mobileImage,
                desktopTransformation: 'Size1900',
                tabletTransformation: 'Size1200',
                mobileTransformation: 'Size1200',
                alt: fields?.image?.value?.alt as string,
                cssClassImageEl: 'slide__image',
                style: { zIndex: sitecoreContext.pageEditing ? 0 : -2 },
                prioritize: (shouldPioritize && !slideInfo) || (shouldPioritize && slideInfo?.index === 0),
              }}
            />
          )}
      <div className={isEE ? 'slide__content slide__content--EE' : 'slide__content'}>
        {sitecoreContext.pageEditing ? (
          <>
            <CustomText field={fields?.title} tag={headingTag} className={'slide__text slide__text--title'} />
            {fields?.content && (
              <RichText field={fields?.content} tag={'span'} className={'slide__text slide__text--content rich-text color-inherit-link'} />
            )}
            <CTAButton type={ctaEnums.primaryRed}>
              <CustomExternalAnchor field={fields?.link} />
            </CTAButton>
          </>
        ) : (
          <>
            <FadeDownEffect delay={1000}>
              <CustomText field={fields?.title} tag={headingTag} className={'slide__text slide__text--title'} />
            </FadeDownEffect>
            {fields?.content && (
              <FadeDownEffect delay={2000}>
                <RichText
                  field={fields?.content}
                  tag={'span'}
                  className={'slide__text slide__text--content rich-text color-inherit-link'}
                />
              </FadeDownEffect>
            )}
            <FadeDownEffect delay={3000}>
              {fields?.link?.value?.href && (
                <CTAButton type={ctaEnums.primaryRed}>
                  <CustomExternalAnchor
                    field={fields.link}
                    onClick={() =>
                      googleClickCtaEvent('Button', fields?.link?.value?.text ?? fields?.link?.value?.title, fields?.title?.value)
                    }
                  />
                </CTAButton>
              )}
            </FadeDownEffect>
          </>
        )}
      </div>
    </div>
  );
};

export default withSitecoreContext()(HeroCarouselSlide);
